import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router-dom";
import {
  Container,
  Box,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  User,
  Lock,
  Building2,
  Users,
  SlidersHorizontal,
} from "lucide-react";
import Profile from "./Settings/Profile";
import Password from "./Settings/Password";
import Company from "./Settings/Company";
import Team from "./Settings/Team";
import CustomSkills from "./Settings/CustomSkills";
import Notifications from "./Settings/Notifications";
import trackEvent from "bundles/common/utils/trackEvent";

const sections = [
  {
    code: "profile",
    labelId: "settings.profile",
    icon: (active) => (
      <User
        size={18}
        color={active ? "hsl(153, 68%, 25%)" : "hsl(220, 10%, 46%)"}
        style={{ marginRight: "10px" }}
      />
    ),
    displayOnMobile: true,
  },
  {
    code: "password",
    labelId: "settings.password",
    icon: (active) => (
      <Lock
        size={18}
        color={active ? "hsl(153, 68%, 25%)" : "hsl(220, 10%, 46%)"}
        style={{ marginRight: "10px" }}
      />
    ),
    displayOnMobile: true,
  },
  {
    code: "company",
    labelId: "settings.company",
    icon: (active) => (
      <Building2
        size={18}
        color={active ? "hsl(153, 68%, 25%)" : "hsl(220, 10%, 46%)"}
        style={{ marginRight: "10px" }}
      />
    ),
    displayOnMobile: false,
  },
  {
    code: "team",
    labelId: "settings.team",
    icon: (active) => (
      <Users
        size={18}
        color={active ? "hsl(153, 68%, 25%)" : "hsl(220, 10%, 46%)"}
        style={{ marginRight: "10px" }}
      />
    ),
    displayOnMobile: false,
  },
  {
    code: "custom_skills",
    labelId: "settings.custom_skills",
    icon: (active) => (
      <SlidersHorizontal
        size={18}
        color={active ? "hsl(153, 68%, 25%)" : "hsl(220, 10%, 46%)"}
        style={{ marginRight: "10px" }}
      />
    ),
    displayOnMobile: false,
  },
  // {
  //   code: "notifications",
  //   labelId: "settings.notifications",
  //   icon: () => <NotificationsIcon />,
  //   displayOnMobile: true,
  // },
];

const useQuery = () => {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
};

const Settings = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const query = useQuery();

  const [selectedSection, setSelectedSection] = useState(
    sections.find((s) => s.code == query.get("selectedSection")) || sections[0]
  );

  const handleSelectSection = (code) => {
    if (code == selectedSection.code) return;
    setSelectedSection(sections.find((s) => s.code == code));
    trackEvent("settings", `opened_${code}`);
  };

  useEffect(() => {
    trackEvent("settings", `opened_${selectedSection.code}`);
    // Mount only: record the section the page was opened on.
  }, []);

  return (
    <Box component="main" sx={{ bgcolor: "#fff", minHeight: "100vh" }}>
      {/* Page header */}
      <Box
        component="section"
        sx={{
          px: { xs: "16px", sm: "24px", lg: "32px" },
          pt: { xs: "32px", md: "48px" },
          pb: { xs: "20px", md: "28px" },
          background: "linear-gradient(to bottom, hsl(178, 30%, 97%), #fff)",
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Box
            component="h1"
            sx={{
              fontFamily: "Segoe UI Black, system-ui, sans-serif",
              fontSize: { xs: "1.5rem", md: "2.25rem" },
              color: "hsl(178, 93%, 10%)",
              letterSpacing: "-0.025em",
              lineHeight: 1.1,
              m: 0,
              mb: "8px",
            }}
          >
            <FormattedMessage id="settings.title" />
          </Box>
          <Box
            component="p"
            sx={{
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              fontSize: { xs: "0.9375rem", md: "1rem" },
              color: "hsl(220, 10%, 46%)",
              m: 0,
            }}
          >
            <FormattedMessage id="settings.subtitle" />
          </Box>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{
          px: { xs: "16px", sm: "24px", lg: "32px" },
          pb: { xs: "48px", md: "64px" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "16px", md: "24px" },
            mt: { xs: "8px", md: "16px" },
          }}
        >
          {/* Navigation */}
          {isDesktop ? (
            <Box
              sx={{
                flexShrink: 0,
                width: "232px",
                alignSelf: "flex-start",
                position: "sticky",
                top: "88px",
                bgcolor: "#fff",
                border: "1px solid hsl(220, 13%, 91%)",
                borderRadius: "12px",
                boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                p: "12px",
              }}
            >
              {sections.map((section) => {
                const active = section.code == selectedSection.code;
                return (
                  <MenuItem
                    key={`section-${section.code}`}
                    disableRipple
                    onClick={() => handleSelectSection(section.code)}
                    sx={{
                      borderRadius: "10px",
                      px: "12px",
                      py: "10px",
                      mb: "4px",
                      "&:last-child": { mb: 0 },
                      bgcolor: active
                        ? "hsla(153, 68%, 25%, 0.08)"
                        : "transparent",
                      "&:hover": {
                        bgcolor: active
                          ? "hsla(153, 68%, 25%, 0.08)"
                          : "hsl(210, 20%, 98%)",
                      },
                    }}
                  >
                    {section.icon(active)}
                    <Box
                      component="span"
                      sx={{
                        fontFamily: active
                          ? "Segoe UI Bold, system-ui, sans-serif"
                          : "Segoe UI Semibold, system-ui, sans-serif",
                        fontSize: "0.9375rem",
                        color: active
                          ? "hsl(153, 68%, 25%)"
                          : "hsl(220, 25%, 12%)",
                      }}
                    >
                      <FormattedMessage id={section.labelId} />
                    </Box>
                  </MenuItem>
                );
              })}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: "8px",
                overflowX: "auto",
                pb: "4px",
              }}
            >
              {sections
                .filter((section) => section.displayOnMobile)
                .map((section) => {
                  const active = section.code == selectedSection.code;
                  return (
                    <Box
                      key={`section-${section.code}`}
                      onClick={() => handleSelectSection(section.code)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        flexShrink: 0,
                        px: "16px",
                        py: "10px",
                        borderRadius: "9999px",
                        border: active
                          ? "1px solid hsl(153, 68%, 25%)"
                          : "1px solid hsl(220, 13%, 91%)",
                        bgcolor: active
                          ? "hsla(153, 68%, 25%, 0.08)"
                          : "#fff",
                      }}
                    >
                      {section.icon(active)}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                          fontSize: "0.875rem",
                          color: active
                            ? "hsl(153, 68%, 25%)"
                            : "hsl(220, 25%, 12%)",
                        }}
                      >
                        <FormattedMessage id={section.labelId} />
                      </Box>
                    </Box>
                  );
                })}
            </Box>
          )}

          {/* Content */}
          <Box
            sx={{
              flexGrow: 1,
              minWidth: 0,
              bgcolor: "#fff",
              border: "1px solid hsl(220, 13%, 91%)",
              borderRadius: "12px",
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
              p: { xs: "20px", md: "32px" },
            }}
          >
            {selectedSection.code == "profile" && <Profile />}
            {selectedSection.code == "password" && <Password />}
            {selectedSection.code == "company" && <Company />}
            {selectedSection.code == "team" && <Team />}
            {selectedSection.code == "notifications" && <Notifications />}
            {selectedSection.code == "custom_skills" && <CustomSkills />}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Settings;
